/**
 * Centralized API endpoint paths for admin portal
 * All endpoints follow the pattern: /admin_portal/:servicesProviderId/:resource/...
 *
 * This file consolidates all API endpoint construction to avoid duplication
 * and make it easier to update paths when backend routes change.
 */

/**
 * Base path for admin portal API endpoints
 */
const ADMIN_PORTAL_BASE = '/admin_portal';

/**
 * Build the base path for a services provider's admin portal endpoints
 */
export const getAdminPortalBasePath = (servicesProviderId: string): string => {
	return `${ADMIN_PORTAL_BASE}/${servicesProviderId}`;
};

/**
 * Auth API endpoints (internal employee authentication)
 */
export const authEndpoints = {
	/**
	 * Base path for auth endpoints
	 * @example /admin_portal/auth
	 */
	base: (): string => `${ADMIN_PORTAL_BASE}/auth`,

	/**
	 * Get current user's session data
	 * @example /admin_portal/auth/me
	 */
	me: (): string => `${authEndpoints.base()}/me`,

	/**
	 * Generate JWT for Mintlify docs authentication (internal and external users)
	 * @example /admin_portal/auth/docs-token
	 */
	docsToken: (): string => `${authEndpoints.base()}/docs-token`,
};

/**
 * Leads API endpoints
 */
export const leadsEndpoints = {
	/**
	 * Base path for leads endpoints
	 * @example /admin_portal/:servicesProviderId/leads
	 */
	base: (servicesProviderId: string): string =>
		`${getAdminPortalBasePath(servicesProviderId)}/leads`,

	/**
	 * Search leads with filters and pagination
	 * @example /admin_portal/:servicesProviderId/leads/search
	 */
	search: (servicesProviderId: string): string =>
		`${leadsEndpoints.base(servicesProviderId)}/search`,

	/**
	 * Get lead stats
	 * @example /admin_portal/:servicesProviderId/leads/stats
	 */
	stats: (servicesProviderId: string): string =>
		`${leadsEndpoints.base(servicesProviderId)}/stats`,

	/**
	 * Export leads
	 * @example /admin_portal/:servicesProviderId/leads/export
	 */
	export: (servicesProviderId: string): string =>
		`${leadsEndpoints.base(servicesProviderId)}/export`,

	/**
	 * Get all pipelines for a services provider
	 * @example /admin_portal/:servicesProviderId/leads/pipelines
	 */
	pipelines: (servicesProviderId: string): string =>
		`${leadsEndpoints.base(servicesProviderId)}/pipelines`,

	/**
	 * Update a lead's stage
	 * @example /admin_portal/:servicesProviderId/leads/:leadId/stage
	 */
	updateStage: (servicesProviderId: string, leadId: string): string =>
		`${leadsEndpoints.base(servicesProviderId)}/${leadId}/stage`,

	/**
	 * Cancel and refund a preorder
	 * @example /admin_portal/:servicesProviderId/leads/refund-preorder
	 */
	refundPreorder: (servicesProviderId: string): string =>
		`${leadsEndpoints.base(servicesProviderId)}/refund-preorder`,

	/**
	 * Get lead details by ID
	 * @example /admin_portal/:servicesProviderId/leads/:leadId
	 */
	details: (servicesProviderId: string, leadId: string): string =>
		`${leadsEndpoints.base(servicesProviderId)}/${leadId}`,

};

/**
 * Conversations API endpoints
 */
export const conversationsEndpoints = {
	/**
	 * Base path for conversations endpoints
	 * @example /admin_portal/:servicesProviderId/conversations
	 */
	base: (servicesProviderId: string): string =>
		`${getAdminPortalBasePath(servicesProviderId)}/conversations`,

	/**
	 * Search conversations with filters and pagination
	 * @example POST /admin_portal/:servicesProviderId/conversations/search
	 */
	search: (servicesProviderId: string): string =>
		`${conversationsEndpoints.base(servicesProviderId)}/search`,

	/**
	 * Get messages for a specific conversation
	 * @example /admin_portal/:servicesProviderId/conversations/:conversationId/messages
	 */
	messages: (servicesProviderId: string, conversationId: string): string =>
		`${conversationsEndpoints.base(servicesProviderId)}/${conversationId}/messages`,

	/**
	 * Get conversation stats for the inbox
	 * @example GET /admin_portal/:servicesProviderId/conversations/stats
	 */
	stats: (servicesProviderId: string): string =>
		`${conversationsEndpoints.base(servicesProviderId)}/stats`,

	/**
	 * Resolve an escalated conversation
	 * @example POST /admin_portal/:servicesProviderId/conversations/:conversationId/resolve
	 */
	resolve: (servicesProviderId: string, conversationId: string): string =>
		`${conversationsEndpoints.base(servicesProviderId)}/${conversationId}/resolve`,
};

/**
 * Orders API endpoints
 */
export const ordersEndpoints = {
	/**
	 * Base path for orders endpoints
	 * @example /admin_portal/:servicesProviderId/orders
	 */
	base: (servicesProviderId: string): string =>
		`${getAdminPortalBasePath(servicesProviderId)}/orders`,

	/**
	 * Search orders
	 * @example /admin_portal/:servicesProviderId/orders/search
	 */
	search: (servicesProviderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/search`,

	/**
	 * Get order stats
	 * @example /admin_portal/:servicesProviderId/orders/stats
	 */
	stats: (servicesProviderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/stats`,

	/**
	 * Export orders
	 * @example /admin_portal/:servicesProviderId/orders/export
	 */
	export: (servicesProviderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/export`,

	/**
	 * Get order details
	 * @example /admin_portal/:servicesProviderId/orders/:orderId/details
	 */
	details: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/details`,

	/**
	 * Get order timeline
	 * @example /admin_portal/:servicesProviderId/orders/:orderId/timeline
	 */
	timeline: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/timeline`,

	/**
	 * Create order timeline note
	 * @example /admin_portal/:servicesProviderId/orders/:orderId/timeline/notes
	 */
	createTimelineNote: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/timeline/notes`,

	/**
	 * Get order progress
	 * @example /admin_portal/:servicesProviderId/orders/:orderId/progress
	 */
	progress: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/progress`,

	/**
	 * Get order payment FAI
	 * @example /admin_portal/:servicesProviderId/orders/:orderId/payment-fai
	 */
	paymentFai: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/payment-fai`,

	/**
	 * Get order documents
	 * @example /admin_portal/:servicesProviderId/orders/:orderId/documents
	 */
	documents: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/documents`,

	/**
	 * Download order document
	 * @example /admin_portal/:servicesProviderId/orders/:orderId/documents/:documentKey/download
	 */
	downloadDocument: (servicesProviderId: string, orderId: string, documentKey: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/documents/${documentKey}/download`,

	// Order progress endpoints
	markReceivedAtFulfillmentCenter: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(
			servicesProviderId,
		)}/${orderId}/mark-received-at-fulfillment-center`,

	markProductionComplete: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/mark-production-complete`,

	markPickupScheduled: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/mark-pickup-scheduled`,

	markPickupFulfilled: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/mark-pickup-fulfilled`,

	markInspected: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/mark-inspected`,

	markInTransitToFulfillmentCenter: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(
			servicesProviderId,
		)}/${orderId}/mark-in-transit-to-fulfillment-center`,

	markInTransitToBuyer: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/mark-in-transit-to-buyer`,

	markDeliveryFulfilled: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/mark-delivery-fulfilled`,

	markBackfillInventoryAddedToInventorySystem: (
		servicesProviderId: string,
		orderId: string,
	): string =>
		`${ordersEndpoints.base(
			servicesProviderId,
		)}/${orderId}/mark-backfill-inventory-added-to-inventory-system`,

	markBackfillAccessoriesRequested: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(
			servicesProviderId,
		)}/${orderId}/mark-backfill-accessories-requested`,

	markAssembled: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/mark-assembled`,

	generateTempTag: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/generate-temp-tag`,

	generatePlate: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/generate-plate`,

	finalizeDealInInventorySystem: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/finalize-deal-in-inventory-system`,

	scanAndUploadForms: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/scan-and-upload-forms`,

	submitSignedTarPaperwork: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/submit-signed-tar-paperwork`,

	completeWarrantyRegistration: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/complete-warranty-registration`,

	completeMSO: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/complete-mso`,

	assignVin: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/assign-vin`,

	assignOdometerReading: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/assign-odometer-reading`,

	assignMotorcycleEngineNumber: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/assign-motorcycle-engine-number`,
	sync: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/sync`,

	shipBuyerPacketToSeller: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/ship-buyer-packet-to-seller`,

	shipSellerFormsToEkho: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/ship-seller-forms-to-ekho`,

	sellerRonSession: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/seller-ron-session`,

	startSellerRonSession: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/start-seller-ron-session`,

	scheduleCounterSignRobosignSession: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(
			servicesProviderId,
		)}/${orderId}/schedule-counter-sign-robosign-session`,

	approveCounterSignRobosignTarPaperwork: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(
			servicesProviderId,
		)}/${orderId}/approve-counter-sign-robosign-tar-paperwork`,

	/**
	 * Update robosign status (approve/reject)
	 * @example /admin_portal/:servicesProviderId/orders/:orderId/update-robosign-status
	 */
	updateRobosignStatus: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/update-robosign-status`,

	/**
	 * Get approve robosign tar paperwork data (buyer session)
	 * @example /admin_portal/:servicesProviderId/orders/:orderId/approve-robosign-tar-paperwork
	 */
	approveRobosignTarPaperwork: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/approve-robosign-tar-paperwork`,

	/**
	 * Download VOS packet
	 * @example /admin_portal/:servicesProviderId/orders/:orderId/download-vos-packet
	 */
	downloadVosPacket: (servicesProviderId: string, orderId: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/download-vos-packet`,

	/**
	 * Download a specific vendor form (POA form)
	 * @example /admin_portal/:servicesProviderId/orders/:orderId/download-vendor-form/:formKey
	 */
	downloadVendorForm: (servicesProviderId: string, orderId: string, formKey: string): string =>
		`${ordersEndpoints.base(servicesProviderId)}/${orderId}/download-vendor-form/${formKey}`,
};

/**
 * Preorders API endpoints
 */
export const preordersEndpoints = {
	/**
	 * Base path for preorders endpoints
	 * @example /admin_portal/:servicesProviderId/preorders
	 */
	base: (servicesProviderId: string): string =>
		`${getAdminPortalBasePath(servicesProviderId)}/preorders`,

	/**
	 * Search preorders
	 * @example /admin_portal/:servicesProviderId/preorders/search
	 */
	search: (servicesProviderId: string): string =>
		`${preordersEndpoints.base(servicesProviderId)}/search`,

	/**
	 * Get preorder stats
	 * @example /admin_portal/:servicesProviderId/preorders/stats
	 */
	stats: (servicesProviderId: string): string =>
		`${preordersEndpoints.base(servicesProviderId)}/stats`,

	/**
	 * Export preorders
	 * @example /admin_portal/:servicesProviderId/preorders/export
	 */
	export: (servicesProviderId: string): string =>
		`${preordersEndpoints.base(servicesProviderId)}/export`,

	/**
	 * Update preorder status
	 * @example /admin_portal/:servicesProviderId/preorders/:preorderId/status
	 */
	updateStatus: (servicesProviderId: string, preorderId: string): string =>
		`${preordersEndpoints.base(servicesProviderId)}/${preorderId}/status`,

	/**
	 * Invite a preorder buyer to checkout
	 * @example POST /admin_portal/:servicesProviderId/preorders/:preorderId/invite
	 */
	invite: (servicesProviderId: string, preorderId: string): string =>
		`${preordersEndpoints.base(servicesProviderId)}/${preorderId}/invite`,

	/**
	 * Get preorder details
	 * @example /admin_portal/:servicesProviderId/preorders/:preorderId/details
	 */
	details: (servicesProviderId: string, preorderId: string): string =>
		`${preordersEndpoints.base(servicesProviderId)}/${preorderId}/details`,

	/**
	 * Cancel and refund a preorder
	 * @example POST /admin_portal/:servicesProviderId/preorders/:preorderId/refund
	 */
	refund: (servicesProviderId: string, preorderId: string): string =>
		`${preordersEndpoints.base(servicesProviderId)}/${preorderId}/refund`,
};

/**
 * Inventory API endpoints
 */
export const inventoryEndpoints = {
	/**
	 * Base path for inventory endpoints
	 * @example /admin_portal/:servicesProviderId/inventory
	 */
	base: (servicesProviderId: string): string =>
		`${getAdminPortalBasePath(servicesProviderId)}/inventory`,

	/**
	 * Publish inventory
	 * @example /admin_portal/:servicesProviderId/inventory/publish-all
	 */
	publishAll: (servicesProviderId: string): string =>
		`${inventoryEndpoints.base(servicesProviderId)}/publish-all`,

	/**
	 * Archive inventory
	 * @example /admin_portal/:servicesProviderId/inventory/archive
	 */
	archive: (servicesProviderId: string): string =>
		`${inventoryEndpoints.base(servicesProviderId)}/archive`,

	/**
	 * Enable online sales
	 * @example /admin_portal/:servicesProviderId/inventory/enable-online-sales
	 */
	enableOnlineSales: (servicesProviderId: string): string =>
		`${inventoryEndpoints.base(servicesProviderId)}/enable-online-sales`,

	/**
	 * Get pricing history for an inventory item
	 * @example /admin_portal/:servicesProviderId/inventory/:inventoryItemId/pricing-history
	 */
	pricingHistory: (servicesProviderId: string, inventoryItemId: string): string =>
		`${inventoryEndpoints.base(servicesProviderId)}/${inventoryItemId}/pricing-history`,

	/**
	 * Sync inventory items (trigger scrape and/or DMS sync)
	 * @example /admin_portal/:servicesProviderId/inventory/sync
	 */
	sync: (servicesProviderId: string): string =>
		`${inventoryEndpoints.base(servicesProviderId)}/sync`,

	/**
	 * Get or patch a single inventory item
	 * @example /admin_portal/:servicesProviderId/inventory/:inventoryItemId
	 */
	detail: (servicesProviderId: string, inventoryItemId: string): string =>
		`${inventoryEndpoints.base(servicesProviderId)}/${inventoryItemId}`,

	/**
	 * Generate a GCS signed URL for cosmetic report image upload
	 * @example POST /admin_portal/:servicesProviderId/inventory/cosmetic-report/upload-url
	 */
	cosmeticReportUploadUrl: (servicesProviderId: string): string =>
		`${inventoryEndpoints.base(servicesProviderId)}/cosmetic-report/upload-url`,

	/**
	 * Generate a GCS signed URL for cosmetic report image display
	 * @example POST /admin_portal/:servicesProviderId/inventory/cosmetic-report/read-url
	 */
	cosmeticReportReadUrl: (servicesProviderId: string): string =>
		`${inventoryEndpoints.base(servicesProviderId)}/cosmetic-report/read-url`,
};

/**
 * Imports API endpoints
 */
export const importsEndpoints = {
	/**
	 * Base path for imports endpoints
	 * @example /admin_portal/:servicesProviderId/imports
	 */
	base: (servicesProviderId: string): string =>
		`${getAdminPortalBasePath(servicesProviderId)}/imports`,

	/**
	 * Import inventory
	 * @example /admin_portal/:servicesProviderId/imports/INVENTORY
	 */
	importInventory: (servicesProviderId: string): string =>
		`${importsEndpoints.base(servicesProviderId)}/INVENTORY`,
};

/**
 * Discounts API endpoints
 */
export const discountsEndpoints = {
	/**
	 * Base path for discounts endpoints
	 * @example /admin_portal/:servicesProviderId/discounts
	 */
	base: (servicesProviderId: string): string =>
		`${getAdminPortalBasePath(servicesProviderId)}/discounts`,

	/**
	 * Search discounts
	 * @example /admin_portal/:servicesProviderId/discounts/search
	 */
	search: (servicesProviderId: string): string =>
		`${discountsEndpoints.base(servicesProviderId)}/search`,

	/**
	 * Export discounts
	 * @example /admin_portal/:servicesProviderId/discounts/export
	 */
	export: (servicesProviderId: string): string =>
		`${discountsEndpoints.base(servicesProviderId)}/export`,

	/**
	 * Get applicable products for discounts
	 * @example /admin_portal/:servicesProviderId/discounts/applicable-products
	 */
	applicableProducts: (servicesProviderId: string): string =>
		`${discountsEndpoints.base(servicesProviderId)}/applicable-products`,

	/**
	 * Create a new discount
	 * @example POST /admin_portal/:servicesProviderId/discounts
	 */
	create: (servicesProviderId: string): string => discountsEndpoints.base(servicesProviderId),

	/**
	 * Get a single discount by ID
	 * @example /admin_portal/:servicesProviderId/discounts/:discountId
	 */
	getDiscount: (servicesProviderId: string, discountId: string): string =>
		`${discountsEndpoints.base(servicesProviderId)}/${encodeURIComponent(discountId)}`,

	/**
	 * Update a discount by ID (PATCH)
	 * @example /admin_portal/:servicesProviderId/discounts/:discountId
	 */
	updateDiscount: (servicesProviderId: string, discountId: string): string =>
		`${discountsEndpoints.base(servicesProviderId)}/${encodeURIComponent(discountId)}`,

	/**
	 * Delete a discount
	 * @example /admin_portal/:servicesProviderId/discounts/:discountId
	 */
	delete: (servicesProviderId: string, discountId: string): string =>
		`${discountsEndpoints.base(servicesProviderId)}/${encodeURIComponent(discountId)}`,
};

/**
 * Reports API endpoints
 */
export const reportsEndpoints = {
	/**
	 * Base path for reports endpoints
	 * @example /admin_portal/:servicesProviderId/reports
	 */
	base: (servicesProviderId: string): string =>
		`${getAdminPortalBasePath(servicesProviderId)}/reports`,

	/**
	 * Generate report
	 * @example /admin_portal/:servicesProviderId/reports/generate
	 */
	generate: (servicesProviderId: string): string =>
		`${reportsEndpoints.base(servicesProviderId)}/generate`,
};

/**
 * Configuration API endpoints
 */
export const configurationEndpoints = {
	/**
	 * Base path for configuration endpoints
	 * @example /admin_portal/:servicesProviderId/configuration
	 */
	base: (servicesProviderId: string): string =>
		`${getAdminPortalBasePath(servicesProviderId)}/configuration`,

	/**
	 * Get full ServicesProvider details
	 * @example /admin_portal/:servicesProviderId/configuration/details
	 */
	details: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/details`,

	/**
	 * Update onboarding page
	 * @example /admin_portal/:servicesProviderId/configuration/onboarding/update-page
	 */
	updateOnboardingPage: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/onboarding/update-page`,

	/**
	 * Update business details
	 * @example /admin_portal/:servicesProviderId/configuration/business-details
	 */
	businessDetails: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/business-details`,

	/**
	 * Create/update titling service
	 * @example /admin_portal/:servicesProviderId/configuration/titling
	 */
	titling: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/titling`,

	/**
	 * Create/update sale service
	 * @example /admin_portal/:servicesProviderId/configuration/sale
	 */
	sale: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/sale`,

	/**
	 * Create/update registration service
	 * @example /admin_portal/:servicesProviderId/configuration/registration
	 */
	registration: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/registration`,

	/**
	 * Create/update pickup service
	 * @example /admin_portal/:servicesProviderId/configuration/pickup
	 */
	pickup: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/pickup`,

	/**
	 * Create/update inspection service
	 * @example /admin_portal/:servicesProviderId/configuration/inspection
	 */
	inspection: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/inspection`,

	/**
	 * Create/update freight service
	 * @example /admin_portal/:servicesProviderId/configuration/freight
	 */
	freight: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/freight`,

	/**
	 * Create/update delivery service
	 * @example /admin_portal/:servicesProviderId/configuration/delivery
	 */
	delivery: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/delivery`,

	/**
	 * Create/update assembly service
	 * @example /admin_portal/:servicesProviderId/configuration/assembly
	 */
	assembly: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/assembly`,

	/**
	 * Create inventory scraper
	 * @example /admin_portal/:servicesProviderId/configuration/inventory/scraper
	 */
	inventoryScraper: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/inventory/scraper`,

	/**
	 * Create inventory field group precedence
	 * @example /admin_portal/:servicesProviderId/configuration/inventory/field-group-precedence
	 */
	inventoryFieldGroupPrecedence: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/inventory/field-group-precedence`,

	/**
	 * Create inventory DMS connection
	 * @example /admin_portal/:servicesProviderId/configuration/inventory/dms-connection
	 */
	inventoryDmsConnection: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/inventory/dms-connection`,

	/**
	 * Update general inventory configuration
	 * @example /admin_portal/:servicesProviderId/configuration/inventory/general-config
	 */
	inventoryGeneralConfig: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/inventory/general-config`,

	/**
	 * Update checkout assignment configuration
	 * @example /admin_portal/:servicesProviderId/configuration/program/checkout/assignment
	 */
	checkoutAssignment: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/program/checkout/assignment`,

	/**
	 * Update checkout fulfillment configuration
	 * @example /admin_portal/:servicesProviderId/configuration/program/checkout/fulfillment
	 */
	checkoutFulfillment: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/program/checkout/fulfillment`,

	/**
	 * Update preorder configuration
	 * @example /admin_portal/:servicesProviderId/configuration/program/preorder
	 */
	preorder: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/program/preorder`,

	/**
	 * Update checkout payment configuration
	 * @example /admin_portal/:servicesProviderId/configuration/program/checkout/payment
	 */
	checkoutPayment: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/program/checkout/payment`,

	/**
	 * Update checkout states supported configuration
	 * @example /admin_portal/:servicesProviderId/configuration/program/checkout/states-supported
	 */
	checkoutStatesSupported: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/program/checkout/states-supported`,

	/**
	 * Update checkout disclosures configuration
	 * @example /admin_portal/:servicesProviderId/configuration/program/checkout/disclosures
	 */
	checkoutDisclosures: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/program/checkout/disclosures`,

	/**
	 * Batch fetch multiple ServicesProvider details
	 * @example POST /admin_portal/configuration/batch-details
	 */
	batchDetails: (): string => `${ADMIN_PORTAL_BASE}/configuration/batch-details`,

	/**
	 * Banking base path
	 * @example /admin_portal/:servicesProviderId/configuration/banking
	 */
	bankingBase: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/banking`,

	/**
	 * Create Plaid link token
	 * @example /admin_portal/:servicesProviderId/configuration/banking/create-link-token
	 */
	bankingCreateLinkToken: (servicesProviderId: string): string =>
		`${configurationEndpoints.bankingBase(servicesProviderId)}/create-link-token`,

	/**
	 * Create bank accounts from Plaid data
	 * @example /admin_portal/:servicesProviderId/configuration/banking/create-bank-accounts-from-plaid-data
	 */
	bankingCreateBankAccountsFromPlaidData: (servicesProviderId: string): string =>
		`${configurationEndpoints.bankingBase(servicesProviderId)}/create-bank-accounts-from-plaid-data`,

	/**
	 * Get bank accounts for a services provider
	 * @example /admin_portal/:servicesProviderId/configuration/banking/bank-accounts
	 */
	bankingGetBankAccounts: (servicesProviderId: string): string =>
		`${configurationEndpoints.bankingBase(servicesProviderId)}/bank-accounts`,

	/**
	 * Set primary bank account
	 * @example /admin_portal/:servicesProviderId/configuration/banking/set-primary-bank-account/:bankAccountId
	 */
	bankingSetPrimaryBankAccount: (servicesProviderId: string, bankAccountId: string): string =>
		`${configurationEndpoints.bankingBase(servicesProviderId)}/set-primary-bank-account/${bankAccountId}`,

	/**
	 * Update BDC service configuration
	 * @example /admin_portal/:servicesProviderId/configuration/bdc
	 */
	bdc: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/bdc`,

	/**
	 * Update VDP configuration for the DOMAINS service (carthage fields)
	 * @example /admin_portal/:servicesProviderId/configuration/domains-vdp-config
	 */
	domainsVdpConfig: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/domains-vdp-config`,

	/**
	 * Get scraped macro information for a services provider
	 * @example /admin_portal/:servicesProviderId/configuration/macro-info
	 */
	macroInfo: (servicesProviderId: string): string =>
		`${configurationEndpoints.base(servicesProviderId)}/macro-info`,
};

/**
 * Team API endpoints
 */
export const teamEndpoints = {
	/**
	 * Base path for team endpoints
	 * @example /admin_portal/:servicesProviderId/team
	 */
	base: (servicesProviderId: string): string =>
		`${getAdminPortalBasePath(servicesProviderId)}/team`,

	/**
	 * Search team members with filters and pagination
	 * @example POST /admin_portal/:servicesProviderId/team/search
	 */
	search: (servicesProviderId: string): string =>
		`${teamEndpoints.base(servicesProviderId)}/search`,

	/**
	 * Create (invite) team members
	 * @example POST /admin_portal/:servicesProviderId/team/members
	 */
	members: (servicesProviderId: string): string =>
		`${teamEndpoints.base(servicesProviderId)}/members`,

	/**
	 * Export team members as CSV
	 * @example POST /admin_portal/:servicesProviderId/team/export
	 */
	export: (servicesProviderId: string): string =>
		`${teamEndpoints.base(servicesProviderId)}/export`,
};

/**
 * Plan API endpoints
 */
export const planEndpoints = {
	/**
	 * Base path for plan endpoints
	 * @example /admin_portal/:servicesProviderId/plan
	 */
	base: (servicesProviderId: string): string =>
		`${getAdminPortalBasePath(servicesProviderId)}/plan`,

	/**
	 * Get credit transactions
	 * @example /admin_portal/:servicesProviderId/plan/credits
	 */
	credits: (servicesProviderId: string): string =>
		`${planEndpoints.base(servicesProviderId)}/credits`,
};

/**
 * Product onboarding API endpoints
 */
export const productOnboardingEndpoints = {
	/**
	 * Base path for product onboarding endpoints
	 * @example /admin_portal/product-onboarding
	 */
	base: (): string => `${ADMIN_PORTAL_BASE}/product-onboarding`,

	/**
	 * Get an onboarding record by ID
	 * @example /admin_portal/product-onboarding/:id
	 */
	record: (id: string): string => `${productOnboardingEndpoints.base()}/${id}`,

	/**
	 * Create a Stripe subscription (net-new dealer, Path 1)
	 * @example POST /admin_portal/product-onboarding/subscribe
	 */
	subscribe: (): string => `${productOnboardingEndpoints.base()}/subscribe`,

	/**
	 * Provision SVP + Auth + Invitation after payment (net-new dealer, Path 1)
	 * @example POST /admin_portal/product-onboarding/:id/provision
	 */
	provision: (id: string): string => `${productOnboardingEndpoints.record(id)}/provision`,

	/**
	 * Get default product pricing (public, no SVP overrides)
	 * @example GET /admin_portal/product-onboarding/pricing
	 */
	pricing: (): string => `${productOnboardingEndpoints.base()}/pricing`,

	/**
	 * Create a Stripe subscription for an existing dealer (Path 2)
	 * @example POST /admin_portal/product-onboarding/:svpId/billing/subscribe
	 */
	svpSubscribe: (svpId: string): string =>
		`${productOnboardingEndpoints.base()}/${svpId}/billing/subscribe`,

	/**
	 * Get product pricing with SVP-specific overrides (existing dealer, Path 2)
	 * @example GET /admin_portal/product-onboarding/:svpId/billing/pricing
	 */
	svpPricing: (svpId: string): string =>
		`${productOnboardingEndpoints.base()}/${svpId}/billing/pricing`,

	/**
	 * Complete BDC onboarding for an existing dealer (Path 2)
	 * @example POST /admin_portal/product-onboarding/:svpId/complete-onboarding
	 */
	completeOnboarding: (svpId: string): string =>
		`${productOnboardingEndpoints.base()}/${svpId}/complete-onboarding`,
};

/**
 * Calendly configuration
 */
export const CALENDLY_URL =
	import.meta.env.VITE_CURR_ENV === 'production'
		? 'https://calendly.com/ekhodealer-support/schedule-your-remote-signing-session'
		: 'https://calendly.com/bongi-ekho/30min';
